import * as React from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRightLeft,
  Ban,
  CheckCircle2,
  Download,
  Heart,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Search,
  Send,
  Trash2,
  UserPlus2,
  Users2,
  X,
} from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import type {
  LearnerInviteItem,
  Student,
  StudentListResponse,
  StudentStatus,
  TenantVerticalLiteral,
} from "@eduviaos/shared";
import { getVerticalProfile } from "@/lib/verticalProfile";
import { useStudentsList } from "@/hooks/useStudents";
import {
  useStudentsLearningSummary,
  type StudentLearningSummaryItem,
} from "@/hooks/useStudentsLearning";
import { StudentLearningDetailDrawer } from "@/components/learning/StudentLearningDetailDrawer";
import { formatRelativeTime } from "@/lib/relativeTime";
import {
  useLearnerInvites,
  useResendLearnerInvite,
  useRevokeLearnerInvite,
} from "@/hooks/useLearnerInvites";
import {
  requiresAppAccessGrant,
  useGrantAppAccess,
  useLearnerAppConfig,
  useRevokeAppAccess,
} from "@/hooks/useLearnerAppConfig";
import { useParent } from "@/hooks/useParents";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { api } from "@/lib/api";
import { ExportCsvErrorBanner } from "@/components/ExportCsvErrorBanner";
import { useCsvExport } from "@/hooks/useCsvExport";
import { StudentEditorSheet } from "@/components/people/StudentEditorSheet";
import { StudentTransitionDialog } from "@/components/people/StudentTransitionDialog";
import { StudentDeleteDialog } from "@/components/people/StudentDeleteDialog";
import { LearnerInviteDialog } from "@/components/people/LearnerInviteDialog";
import { ParentEditorSheet } from "@/components/people/ParentEditorSheet";

/**
 * Phase J1 + J1.b — Students roster inside `<PeoplePage>`.
 *
 * Read + write surface. Filters: status (server-side), grade
 * (server-side), section (server-side), and a client-side `q` name
 * filter applied to the loaded page (the BE `/api/students/` v1
 * surface doesn't carry `?q=`; a future BE patch will promote this
 * to server-side).
 *
 * Pagination: 50/page default with offset/limit; footer shows the
 * loaded slice + total.
 *
 * J1.b writes:
 *   - "Add student" toolbar button → `<StudentEditorSheet>` in create mode.
 *   - Row action menu (per row, `…`):
 *       - View          (no-op placeholder — drawer detail lands in J2/J3)
 *       - Edit          → `<StudentEditorSheet>` in edit mode
 *       - Change status → `<StudentTransitionDialog>` (audited)
 *       - Remove        → `<StudentDeleteDialog>` (soft-delete, confirm)
 *
 * Write affordances are gated on `canManage` (caller passes
 * `manage_students` permission). When `canManage` is false the row
 * action column collapses to a hyphen and the "Add student" button
 * is hidden — mirrors the toolbar gating in `ImportStudentsPage`.
 *
 * Iron Rule #16: uses the `<DataTable>` primitive — sticky thead +
 * `<StatusPill>` for status + action column on the right.
 *
 * Phase E1.f — pagination uses the shared `<DataTablePagination>`
 * primitive with a user-selectable rows-per-page (default 50,
 * options 10/25/50/100; persisted per-user via localStorage scope
 * `students`).
 */

const STATUS_FILTER_OPTIONS: Array<{ value: "__any__" | StudentStatus; label: string }> = [
  { value: "__any__", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "graduated", label: "Graduated" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "transferred", label: "Transferred" },
];

/**
 * Map the BE `StudentStatus` enum to the `<StatusPill>` palette.
 * Defensive default keeps unknown statuses readable as Inactive
 * instead of throwing.
 */
function statusPillVariant(status: StudentStatus): StatusPillStatus {
  switch (status) {
    case "active":
      return "active";
    case "paused":
      return "invited";
    case "graduated":
    case "withdrawn":
    case "transferred":
      return "archived";
    default:
      return "inactive";
  }
}

function statusPillLabel(status: StudentStatus): string {
  switch (status) {
    case "active":
      return "Active";
    case "paused":
      return "Paused";
    case "graduated":
      return "Graduated";
    case "withdrawn":
      return "Withdrawn";
    case "transferred":
      return "Transferred";
    default:
      return status;
  }
}

function parentNameFromMetadata(student: Student): string | null {
  const raw = student.metadata?.["parent_name"];
  return typeof raw === "string" && raw.trim().length > 0 ? raw : null;
}

/**
 * Phase SC-4c — "does this learner have the app?" in one glance.
 *
 * Three states that matter operationally, and they come from two
 * documents: `Student.user_id` is set once an invite is redeemed (the
 * binding the portal's self-scoped reads depend on), while the invite row
 * records the in-between where a link is out but unclaimed.
 */
function PortalAccessCell({
  student,
  invite,
  loading,
  requiresAppGrant = false,
  appAccessGranted = true,
}: {
  student: Student;
  invite: LearnerInviteItem | null;
  loading: boolean;
  requiresAppGrant?: boolean;
  appAccessGranted?: boolean;
}) {
  if (student.user_id) {
    if (requiresAppGrant && !appAccessGranted) {
      return <StatusPill status="invited" label="Payment pending" />;
    }
    if (requiresAppGrant && appAccessGranted) {
      return <StatusPill status="active" label="App access" />;
    }
    return <StatusPill status="active" label="Has access" />;
  }
  if (invite?.status === "pending") {
    return <StatusPill status="invited" label="Invited" />;
  }
  if (invite?.status === "expired") {
    return <StatusPill status="inactive" label="Invite expired" />;
  }
  if (invite?.status === "revoked") {
    return <StatusPill status="inactive" label="Invite revoked" />;
  }
  if (loading) {
    return <span className="text-muted-foreground text-xs">…</span>;
  }
  return <span className="text-muted-foreground text-xs">No access</span>;
}

/**
 * Row-menu items for portal access. Which action is offered depends on
 * the same three states: nothing to do once they're in, resend/revoke
 * while a link is outstanding, invite otherwise.
 */
function PortalInviteActions({
  student,
  invite,
  onInvite,
}: {
  student: Student;
  invite: LearnerInviteItem | null;
  onInvite: () => void;
}) {
  const resend = useResendLearnerInvite();
  const revoke = useRevokeLearnerInvite();

  if (student.user_id) return null;

  if (invite?.status === "pending") {
    return (
      <>
        <DropdownMenuItem
          onSelect={() => {
            resend
              .mutateAsync(invite.id)
              .then(() => toast.success(`Invite re-sent to ${invite.email}.`))
              .catch(() => toast.error("Couldn't re-send that invite. Try again in a moment."));
          }}
          data-testid={`student-action-resend-invite-${student.id}`}
        >
          <Send aria-hidden="true" className="size-4" />
          Re-send portal invite
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            revoke
              .mutateAsync(invite.id)
              .then(() => toast.success(`Invite for ${student.name} revoked.`))
              .catch(() => toast.error("Couldn't revoke that invite. Try again in a moment."));
          }}
          data-testid={`student-action-revoke-invite-${student.id}`}
        >
          <Ban aria-hidden="true" className="size-4" />
          Revoke portal invite
        </DropdownMenuItem>
      </>
    );
  }

  return (
    <DropdownMenuItem onSelect={onInvite} data-testid={`student-action-invite-${student.id}`}>
      <Send aria-hidden="true" className="size-4" />
      {invite ? "Invite to portal again" : "Invite to portal"}
    </DropdownMenuItem>
  );
}

function AppAccessActions({
  student,
  appAccessGranted,
  onGrantedChange,
  onRevoke,
}: {
  student: Student;
  appAccessGranted: boolean;
  onGrantedChange: (studentId: string, granted: boolean) => void;
  onRevoke: (student: Student) => void;
}) {
  const grant = useGrantAppAccess();

  if (!student.user_id) return null;

  if (appAccessGranted) {
    return (
      <DropdownMenuItem
        onSelect={() => onRevoke(student)}
        data-testid={`student-action-revoke-app-${student.id}`}
      >
        <Ban aria-hidden="true" className="size-4" />
        Revoke app access
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      onSelect={() => {
        grant
          .mutateAsync(student.id)
          .then((res) => {
            onGrantedChange(student.id, res.app_access_granted);
            toast.success(`App access granted for ${student.name}.`);
          })
          .catch(() => toast.error("Couldn't grant app access. Try again."));
      }}
      data-testid={`student-action-grant-app-${student.id}`}
    >
      <CheckCircle2 aria-hidden="true" className="size-4" />
      Grant app access
    </DropdownMenuItem>
  );
}

export interface StudentsTabProps {
  /** Set to true when the active user has `manage_students`. */
  canManage?: boolean;
  /**
   * Phase J4.b — Canonical tenant slug used in the CSV export filename
   * (`students-<tenant_slug>-<YYYY-MM-DD>.csv`). Defaults to `null`
   * which renders as the generic `students-export-<date>.csv` fallback.
   */
  tenantSlug?: string | null;
  /**
   * SC Foundation (F0b) — the active tenant's vertical. Drives the
   * vertical profile that de-schools the roster: a study centre has no
   * grades, sections, or parents, so those columns + filter inputs are
   * dropped and the copy speaks in "Learner" vocabulary. Defaults to
   * `school` so existing school/coaching behaviour (and the direct-render
   * tests that don't pass a vertical) is byte-for-byte unchanged.
   */
  vertical?: TenantVerticalLiteral;
  /** SC-21e — show learning rollup columns when study centre + learning module. */
  learningModuleEnabled?: boolean;
}

export function StudentsTab({
  canManage = false,
  tenantSlug = null,
  vertical = "school",
  learningModuleEnabled = false,
}: StudentsTabProps = {}) {
  const profile = getVerticalProfile(vertical);
  const terms = profile.terms;
  const showGrade = profile.has.grades;
  const showSection = profile.has.sections;
  const showParent = profile.has.parents;
  const showPortal = profile.has.learnerPortal;
  const showLearningCols = learningModuleEnabled && showPortal;
  const hasSchoolCols = showGrade || showSection || showParent;
  const personOne = profile.personNoun.one.toLowerCase();
  const personMany = profile.personNoun.many.toLowerCase();
  // Skeleton column parity: name, roll, email/phone, status, action are
  // always present (5); grade/section/parent/portal are conditional.
  const columnCount =
    5 +
    (showGrade ? 1 : 0) +
    (showSection ? 1 : 0) +
    (showParent ? 1 : 0) +
    (showPortal ? 1 : 0) +
    (showLearningCols ? 3 : 0);
  const [statusFilter, setStatusFilter] = React.useState<"__any__" | StudentStatus>("__any__");
  const [gradeFilter, setGradeFilter] = React.useState("");
  const [sectionFilter, setSectionFilter] = React.useState("");
  const [qInput, setQInput] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "students",
    defaultPageSize: 10,
  });

  // Phase J3.d — Household filter sourced from the URL (`?parent_id=…`)
  // so the Parents tab "View linked students" drawer can deep-link into
  // a household-scoped Students view. Read-only URL state; we don't push
  // local filter changes into the URL (status/grade/section stay local
  // by design — they're per-session and would clutter shareable links).
  // When `parent_id` is present, the inline filter row shows a
  // dismissable "Household: {parent name}" chip; the X button strips
  // `parent_id` from the URL without disturbing `tab=students`.
  const [searchParams, setSearchParams] = useSearchParams();
  const parentIdFilter = searchParams.get("parent_id");
  const parentLookupQuery = useParent(parentIdFilter);
  const parentLookup = parentLookupQuery.data;

  const clearParentIdFilter = React.useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("parent_id");
        return next;
      },
      { replace: true },
    );
    setPage(0);
  }, [setSearchParams]);

  type DialogState =
    | { kind: "none" }
    | { kind: "create" }
    | { kind: "edit"; student: Student }
    | { kind: "transition"; student: Student }
    | { kind: "delete"; student: Student }
    | { kind: "revoke-app"; student: Student }
    // Phase SC-4c — give this learner a portal login. A dialog rather
    // than a straight mutation because imported rows often have no email
    // (or carry a parent's), and the admin has to confirm where the
    // invite is going before it goes.
    | { kind: "invite"; student: Student }
    // Phase J3.e — Symmetric counterpart to J3.d. The Parent column on
    // each Student row becomes a clickable affordance (gated on
    // `canManage`) that opens the existing `<ParentEditorSheet>` for the
    // student's linked parent. Closes the bidirectional Student↔Parent
    // navigation loop: J3.c+J3.d gave us Parent → linked students; J3.e
    // gives us Student → linked parent.
    | { kind: "view-parent"; parentId: string }
    | { kind: "learning-detail"; studentId: string; studentName: string };
  const [dialog, setDialog] = React.useState<DialogState>({ kind: "none" });

  // Resolve the parent for the J3.e Parent-link dialog. Null when no
  // dialog is open — the `useParent` hook is enabled iff a non-null id
  // is passed (matches the J3.d household-chip pattern).
  const rowParentId = dialog.kind === "view-parent" ? dialog.parentId : null;
  const rowParentQuery = useParent(rowParentId);
  const rowParent = rowParentQuery.data ?? null;

  const filters = React.useMemo(
    () => ({
      status: statusFilter === "__any__" ? undefined : statusFilter,
      grade: gradeFilter.trim() || undefined,
      section: sectionFilter.trim() || undefined,
      parent_id: parentIdFilter ?? undefined,
      limit: pageSize,
      offset: page * pageSize,
    }),
    [statusFilter, gradeFilter, sectionFilter, parentIdFilter, page, pageSize],
  );

  const query = useStudentsList(filters);
  const learningSummaryQuery = useStudentsLearningSummary(filters, showLearningCols);
  const summaryByStudent = React.useMemo(() => {
    const map = new Map<string, StudentLearningSummaryItem>();
    for (const row of learningSummaryQuery.data?.items ?? []) {
      map.set(row.student_id, row);
    }
    return map;
  }, [learningSummaryQuery.data]);
  const { data, isLoading, isFetching, isError, error, refetch } = query;

  // Portal access lives on a separate document (the invite), so the
  // roster joins the two client-side: `Student.user_id` answers "has a
  // login", the invite answers "was asked, hasn't accepted". One fetch
  // for the whole tenant, only where the column is rendered at all.
  const learnerInvitesQuery = useLearnerInvites(showPortal);
  const learnerAppConfigQuery = useLearnerAppConfig(showPortal);
  const requiresAppGrant = requiresAppAccessGrant(learnerAppConfigQuery.data?.pricing_mode);
  const [appAccessByStudent, setAppAccessByStudent] = React.useState<Record<string, boolean>>({});

  const handleAppAccessChange = React.useCallback((studentId: string, granted: boolean) => {
    setAppAccessByStudent((prev) => ({ ...prev, [studentId]: granted }));
  }, []);

  React.useEffect(() => {
    if (!requiresAppGrant || !data?.items) return;
    setAppAccessByStudent((prev) => {
      const next = { ...prev };
      for (const student of data.items) {
        if (student.app_access_granted != null) {
          next[student.id] = student.app_access_granted;
        }
      }
      return next;
    });
  }, [data?.items, requiresAppGrant]);
  const inviteByStudent = React.useMemo(() => {
    const map = new Map<string, LearnerInviteItem>();
    for (const item of learnerInvitesQuery.data?.items ?? []) {
      // Newest-first from the BE; keep the first (latest) per student so a
      // revoked-then-reissued learner reads as pending, not revoked.
      if (!map.has(item.student_id)) map.set(item.student_id, item);
    }
    return map;
  }, [learnerInvitesQuery.data]);

  const total = data?.total ?? 0;
  const allItems = React.useMemo(() => data?.items ?? [], [data]);

  const filteredItems = React.useMemo(() => {
    const needle = qInput.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter((s) => {
      const haystack = [
        s.name,
        s.email ?? "",
        s.phone ?? "",
        s.roll_number ?? "",
        s.admission_number ?? "",
        parentNameFromMetadata(s) ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [allItems, qInput]);

  const hasFiltersApplied =
    statusFilter !== "__any__" ||
    gradeFilter !== "" ||
    sectionFilter !== "" ||
    qInput !== "" ||
    parentIdFilter !== null;

  const clearFilters = React.useCallback(() => {
    setStatusFilter("__any__");
    setGradeFilter("");
    setSectionFilter("");
    setQInput("");
    setPage(0);
    clearParentIdFilter();
  }, [clearParentIdFilter]);

  // Phase J4.b — CSV export of the filtered students roster.
  // Mirrors the J4.a Staff tab implementation: iteratively fetches
  // server-side-filtered pages in 200-row chunks, honors the live
  // status / grade / section / parent_id filters, then applies the
  // existing client-side `q` filter to the collected superset (the
  // BE `/api/students/` v1 surface doesn't yet carry `?q=`; once it
  // does, this loop can pass it through directly and the client-side
  // filter falls out). Output filename:
  // `students-<tenant_slug>-<YYYY-MM-DD>.csv`. Errors surface through
  // a dismissable inline banner.
  // Phase D2.f — CSV export wiring delegated to the shared
  // `useCsvExport` primitive. The page keeps full ownership of:
  //
  //   - The data composition (iterative fetch loop + client-side `q`
  //     filter + column/row shape).
  //   - The disabled-state precondition (empty roster).
  //   - The error message shape (`Couldn't export the students roster
  //     as CSV: <detail>` with colon-vs-period distinction).
  //
  // The hook owns: state machine, `now` pinning, filename, the
  // `buildCsv` + `downloadCsv` invocation, error capture. Preserving
  // the existing message shape via `formatError` keeps the existing
  // Vitest assertions (which use `toHaveTextContent`) and the
  // user-visible copy untouched.
  const csvExport = useCsvExport({
    filenamePrefix: "students",
    tenantSlug,
    disabled: total === 0 && !isLoading,
    formatError: (err) =>
      err instanceof Error && err.message
        ? `Couldn't export the ${personMany} roster as CSV: ${err.message}`
        : `Couldn't export the ${personMany} roster as CSV.`,
    buildRows: async () => {
      const collected: Student[] = [];
      const CHUNK = 200;
      const HARD_CAP = 50_000;
      let offset = 0;
      let expectedTotal: number | null = null;
      while (collected.length < HARD_CAP) {
        const search = new URLSearchParams();
        if (statusFilter !== "__any__") search.set("status", statusFilter);
        if (gradeFilter.trim()) search.set("grade", gradeFilter.trim());
        if (sectionFilter.trim()) search.set("section", sectionFilter.trim());
        if (parentIdFilter) search.set("parent_id", parentIdFilter);
        search.set("limit", String(CHUNK));
        search.set("offset", String(offset));
        const page = await api.get<StudentListResponse>(`/api/students/?${search.toString()}`);
        collected.push(...page.items);
        if (expectedTotal === null) expectedTotal = page.total;
        offset += page.items.length;
        if (page.items.length === 0) break;
        if (page.items.length < CHUNK) break;
        if (expectedTotal !== null && collected.length >= expectedTotal) break;
      }

      const needle = qInput.trim().toLowerCase();
      const rows = needle
        ? collected.filter((s) => {
            const haystack = [
              s.name,
              s.email ?? "",
              s.phone ?? "",
              s.roll_number ?? "",
              s.admission_number ?? "",
              parentNameFromMetadata(s) ?? "",
            ]
              .join(" ")
              .toLowerCase();
            return haystack.includes(needle);
          })
        : collected;

      const exportColumns: ReadonlyArray<{
        header: string;
        include: boolean;
        value: (s: Student) => string | null;
      }> = [
        { header: "Name", include: true, value: (s) => s.name },
        { header: terms.enrolmentId, include: true, value: (s) => s.roll_number },
        { header: terms.membershipNo, include: true, value: (s) => s.admission_number },
        { header: terms.grade, include: showGrade, value: (s) => s.grade },
        { header: terms.section, include: showSection, value: (s) => s.section },
        { header: "Email", include: true, value: (s) => s.email },
        { header: "Phone", include: true, value: (s) => s.phone },
        { header: "Date of birth", include: true, value: (s) => s.date_of_birth },
        { header: "Gender", include: true, value: (s) => s.gender },
        { header: "Status", include: true, value: (s) => s.status },
        { header: terms.joiningDate, include: true, value: (s) => s.admission_date },
        {
          header: `${terms.guardian.one} name`,
          include: showParent,
          value: (s) => parentNameFromMetadata(s),
        },
        { header: "Created at", include: true, value: (s) => s.created_at },
      ];
      const included = exportColumns.filter((c) => c.include);
      return {
        columns: included.map((c) => c.header),
        rows: rows.map((s) => included.map((c) => c.value(s))),
      };
    },
  });

  const exportDisabled = csvExport.isExporting || (total === 0 && !isLoading);

  return (
    <div data-testid="people-students-tab">
      <ExportCsvErrorBanner
        variant="inline"
        testid="students-export-error"
        error={csvExport.exportError}
        onDismiss={csvExport.dismissError}
        className="mb-4"
      />

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mb-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn&apos;t load {personMany}
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable responsive="cards">
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder={`Search name, email, phone, ${terms.enrolmentId.toLowerCase()}…`}
              className="pl-8"
              aria-label={`Search ${personMany}`}
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as typeof statusFilter);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-36" aria-label="Status filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showGrade ? (
            <Input
              type="search"
              value={gradeFilter}
              onChange={(e) => {
                setGradeFilter(e.target.value);
                setPage(0);
              }}
              placeholder="Grade"
              className="h-9 w-20"
              aria-label="Grade filter"
            />
          ) : null}
          {showSection ? (
            <Input
              type="search"
              value={sectionFilter}
              onChange={(e) => {
                setSectionFilter(e.target.value);
                setPage(0);
              }}
              placeholder="Section"
              className="h-9 w-20"
              aria-label="Section filter"
            />
          ) : null}
          {parentIdFilter ? (
            <span
              className="border-border bg-muted/40 text-foreground inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs"
              data-testid="students-household-chip"
            >
              <Heart aria-hidden="true" className="text-muted-foreground size-3" />
              <span className="text-muted-foreground">Household:</span>
              <span className="max-w-[140px] truncate font-medium">
                {parentLookupQuery.isLoading
                  ? "loading…"
                  : (parentLookup?.name ?? `unknown ${terms.guardian.one.toLowerCase()}`)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={clearParentIdFilter}
                aria-label="Clear household filter"
                data-testid="students-household-chip-clear"
                className="-mr-1 size-4"
              >
                <X aria-hidden="true" className="size-3" />
              </Button>
            </span>
          ) : null}
          {hasFiltersApplied ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <Button
            variant="ghost"
            size="sm"
            onClick={csvExport.handleExport}
            disabled={exportDisabled}
            aria-label={`Download ${personMany} roster as CSV`}
            data-testid="students-export-csv"
          >
            <Download
              aria-hidden="true"
              className={cn("size-4", csvExport.isExporting && "animate-pulse")}
            />
            {csvExport.isExporting ? "Exporting…" : "Download CSV"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw aria-hidden="true" className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
          {canManage ? (
            <Button
              size="sm"
              onClick={() => setDialog({ kind: "create" })}
              data-testid="students-add-button"
            >
              <UserPlus2 aria-hidden="true" className="size-4" />
              Add {personOne}
            </Button>
          ) : null}
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={hasSchoolCols ? "w-[24%]" : "w-[34%]"}>Name</TableHead>
                <TableHead className="w-[10%]">{terms.enrolmentId}</TableHead>
                {showGrade ? <TableHead className="w-[7%]">{terms.grade}</TableHead> : null}
                {showSection ? <TableHead className="w-[7%]">{terms.section}</TableHead> : null}
                <TableHead className={hasSchoolCols ? "w-[18%]" : "w-[30%]"}>
                  Email / Phone
                </TableHead>
                {showParent ? (
                  <TableHead className="w-[18%]">{terms.guardian.one}</TableHead>
                ) : null}
                <TableHead className={hasSchoolCols ? "w-[10%]" : "w-[14%]"}>Status</TableHead>
                {showPortal ? <TableHead className="w-[12%]">Portal</TableHead> : null}
                {showLearningCols ? (
                  <>
                    <TableHead className="w-[7%] text-right">Plans</TableHead>
                    <TableHead className="w-[7%] text-right">Due</TableHead>
                    <TableHead className="w-[14%]">Last active</TableHead>
                  </>
                ) : null}
                <TableHead className="w-[6%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={columnCount} />
            ) : (
              <TableBody>
                {filteredItems.map((s) => {
                  const parentName = parentNameFromMetadata(s);
                  const learning = summaryByStudent.get(s.id);
                  return (
                    <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                      <TableCell className="font-medium">
                        {showLearningCols ? (
                          <button
                            type="button"
                            onClick={() =>
                              setDialog({
                                kind: "learning-detail",
                                studentId: s.id,
                                studentName: s.name,
                              })
                            }
                            className="hover:text-primary text-left hover:underline focus-visible:underline focus-visible:outline-none"
                            data-testid={`student-row-learning-${s.id}`}
                          >
                            {s.name}
                          </button>
                        ) : (
                          s.name
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs tabular-nums">
                        {s.roll_number ?? "—"}
                      </TableCell>
                      {showGrade ? (
                        <TableCell className="tabular-nums">{s.grade ?? "—"}</TableCell>
                      ) : null}
                      {showSection ? <TableCell>{s.section ?? "—"}</TableCell> : null}
                      <TableCell className="text-muted-foreground text-xs">
                        <div className="truncate">{s.email ?? "—"}</div>
                        <div className="truncate">{s.phone ?? ""}</div>
                      </TableCell>
                      {showParent ? (
                        <TableCell className="text-muted-foreground text-xs">
                          {/* Phase J3.e — Clickable parent link when the
                            student has a linked parent_id AND the user
                            can manage. We still show the denormalized
                            `metadata.parent_name` for the label (cheap
                            render, no extra fetch on list paint); the
                            click resolves the parent by id via
                            `useParent` and opens the editor sheet.
                            Falls back to plain text when parent_id is
                            null or canManage is false. */}
                          {s.parent_id && canManage ? (
                            <button
                              type="button"
                              onClick={() =>
                                setDialog({ kind: "view-parent", parentId: s.parent_id! })
                              }
                              className="hover:text-foreground hover:underline focus-visible:underline focus-visible:outline-none"
                              data-testid={`student-row-parent-link-${s.id}`}
                              aria-label={`Open ${terms.guardian.one.toLowerCase()} profile for ${s.name}`}
                            >
                              {parentName ?? `View ${terms.guardian.one.toLowerCase()}`}
                            </button>
                          ) : (
                            (parentName ?? "—")
                          )}
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <StatusPill
                          status={statusPillVariant(s.status)}
                          label={statusPillLabel(s.status)}
                        />
                      </TableCell>
                      {showPortal ? (
                        <TableCell>
                          <PortalAccessCell
                            student={s}
                            invite={inviteByStudent.get(s.id) ?? null}
                            loading={learnerInvitesQuery.isLoading}
                            requiresAppGrant={requiresAppGrant}
                            appAccessGranted={appAccessByStudent[s.id] ?? false}
                          />
                        </TableCell>
                      ) : null}
                      {showLearningCols ? (
                        <>
                          <TableCell className="text-right tabular-nums">
                            {learning?.has_portal_login ? learning.active_plans : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {learning?.has_portal_login ? learning.due_cards : "—"}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {learning?.last_active_at
                              ? formatRelativeTime(learning.last_active_at)
                              : learning?.has_portal_login
                                ? "No activity"
                                : "—"}
                          </TableCell>
                        </>
                      ) : null}
                      <TableCell className="text-right">
                        {canManage ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Actions for ${s.name}`}
                                data-testid={`student-row-actions-${s.id}`}
                                className="size-7"
                              >
                                <MoreHorizontal aria-hidden="true" className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              <DropdownMenuItem
                                onSelect={() => setDialog({ kind: "edit", student: s })}
                                data-testid={`student-action-edit-${s.id}`}
                              >
                                <Pencil aria-hidden="true" className="size-4" />
                                Edit
                              </DropdownMenuItem>
                              {showPortal ? (
                                <PortalInviteActions
                                  student={s}
                                  invite={inviteByStudent.get(s.id) ?? null}
                                  onInvite={() => setDialog({ kind: "invite", student: s })}
                                />
                              ) : null}
                              {showPortal && requiresAppGrant ? (
                                <AppAccessActions
                                  student={s}
                                  appAccessGranted={appAccessByStudent[s.id] ?? false}
                                  onGrantedChange={handleAppAccessChange}
                                  onRevoke={(student) => setDialog({ kind: "revoke-app", student })}
                                />
                              ) : null}
                              <DropdownMenuItem
                                onSelect={() => setDialog({ kind: "transition", student: s })}
                                data-testid={`student-action-transition-${s.id}`}
                              >
                                <ArrowRightLeft aria-hidden="true" className="size-4" />
                                Change status
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() => setDialog({ kind: "delete", student: s })}
                                data-testid={`student-action-delete-${s.id}`}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 aria-hidden="true" className="size-4" />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && filteredItems.length === 0 ? (
          <DataTableEmpty
            icon={<Users2 aria-hidden="true" />}
            title={hasFiltersApplied ? "No matches" : `No ${personMany} yet`}
            description={
              hasFiltersApplied
                ? `Adjust filters or clear them to see every ${personOne}.`
                : `Imported ${personMany} show up here. Use Import ${personMany} to bulk-add from a spreadsheet.`
            }
            action={
              hasFiltersApplied ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : canManage ? (
                <Button size="sm" onClick={() => setDialog({ kind: "create" })}>
                  <UserPlus2 aria-hidden="true" className="size-4" />
                  Add {personOne}
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && filteredItems.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {filteredItems.length.toLocaleString()} of {total.toLocaleString()}
            </span>
            <DataTablePagination
              className="ml-auto"
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              isFetching={isFetching}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                // Reset to first page so the user doesn't land in an
                // empty mid-list slice after switching from 50 → 10
                // (offset 50 with limit 10 on a 60-row list = empty).
                setPage(0);
              }}
              testIdPrefix="students-pagination"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      {canManage ? (
        <>
          <StudentEditorSheet
            open={dialog.kind === "create" || dialog.kind === "edit"}
            onOpenChange={(next) => {
              if (!next) setDialog({ kind: "none" });
            }}
            existing={dialog.kind === "edit" ? dialog.student : null}
            onSaved={() => setDialog({ kind: "none" })}
            vertical={vertical}
          />
          <StudentTransitionDialog
            open={dialog.kind === "transition"}
            onOpenChange={(next) => {
              if (!next) setDialog({ kind: "none" });
            }}
            vertical={vertical}
            student={dialog.kind === "transition" ? dialog.student : null}
            onTransitioned={() => setDialog({ kind: "none" })}
          />
          <LearnerInviteDialog
            open={dialog.kind === "invite"}
            onOpenChange={(next) => {
              if (!next) setDialog({ kind: "none" });
            }}
            student={dialog.kind === "invite" ? dialog.student : null}
            personNoun={personOne}
            onInvited={() => setDialog({ kind: "none" })}
          />
          <StudentDeleteDialog
            open={dialog.kind === "delete"}
            onOpenChange={(next) => {
              if (!next) setDialog({ kind: "none" });
            }}
            student={dialog.kind === "delete" ? dialog.student : null}
            vertical={vertical}
            onDeleted={() => setDialog({ kind: "none" })}
          />
          <RevokeAppAccessDialog
            open={dialog.kind === "revoke-app"}
            onOpenChange={(next) => {
              if (!next) setDialog({ kind: "none" });
            }}
            student={dialog.kind === "revoke-app" ? dialog.student : null}
            onRevoked={(studentId) => {
              handleAppAccessChange(studentId, false);
              setDialog({ kind: "none" });
            }}
          />
          {/* Phase J3.e — Parent editor mounted lazily for the Student
              row's clickable Parent cell. The sheet only opens once the
              parent_id is resolved by `useParent`; while resolving we
              keep the sheet closed (the click feedback is a hover state
              on the trigger, so a sub-second cold-cache lookup is fine
              without a separate spinner). On stage the most common
              path is an instant cache hit because the J3 Parents tab
              already populated the parents list query. */}
          <ParentEditorSheet
            open={dialog.kind === "view-parent" && Boolean(rowParent)}
            onOpenChange={(next) => {
              if (!next) setDialog({ kind: "none" });
            }}
            existing={rowParent}
            onSaved={() => setDialog({ kind: "none" })}
          />
        </>
      ) : null}

      <StudentLearningDetailDrawer
        open={dialog.kind === "learning-detail"}
        onOpenChange={(next) => {
          if (!next) setDialog({ kind: "none" });
        }}
        studentId={dialog.kind === "learning-detail" ? dialog.studentId : null}
        studentName={dialog.kind === "learning-detail" ? dialog.studentName : null}
      />
    </div>
  );
}

function RevokeAppAccessDialog({
  open,
  onOpenChange,
  student,
  onRevoked,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student: Student | null;
  onRevoked: (studentId: string) => void;
}) {
  const revoke = useRevokeAppAccess();

  const confirm = async () => {
    if (!student) return;
    try {
      await revoke.mutateAsync(student.id);
      toast.success(`App access revoked for ${student.name}.`);
      onRevoked(student.id);
    } catch {
      toast.error("Couldn't revoke app access. Try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke app access</DialogTitle>
          <DialogDescription>
            {student?.name ?? "This learner"} will see the paywall on Learn until you grant access
            again. Their plans and history stay.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={revoke.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void confirm()} disabled={revoke.isPending}>
            Revoke access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
